/**
 * EmptyState — the house empty-state card (ui/EmptyState.jsx, re-exported
 * here): what's absent, how it comes to exist (or how to recover), and an
 * optional action.
 *
 * `useTenantEmpty` + `ConnectEndpointsAction` let a first-use empty state
 * on an endpoint-derived surface tell the truth about WHY it's empty: on
 * a tenant with no endpoints ever reporting, "nothing in this scope" is
 * misdirection — the tenant is unenrolled and the fix is the Connect
 * surface. Gate the hook on "this surface is actually rendering empty"
 * so the connect-state fetch is only spent when the answer matters.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { buttonClass } from "./ui/Button.jsx";
import { useConnectState } from "../lib/connectState.js";
import { listDirectoryIntegrations } from "../lib/identityClient.js";
import { hasActiveDirectory } from "../pages/dataSources/model.js";

// The box itself is the kit's (ui/EmptyState.jsx).
export { default } from "./ui/EmptyState.jsx";

// True only on a CONFIRMED zero-endpoint tenant — unknown/loading/denied
// all read false, so a surface never claims "not connected" on a hunch.
// Pass `enabled: false` while the surface has rows or is still loading;
// the connect-state fetch fires only once the empty state is real.
export function useTenantEmpty({ enabled = true } = {}) {
    const connect = useConnectState({ enabled });
    return connect.status === "ready" && connect.connected === false;
}

export function useNoDirectory({ enabled = true } = {}) {
    const [state, setState] = useState(null);
    useEffect(() => {
        if (!enabled || state !== null) return undefined;
        let live = true;
        listDirectoryIntegrations().then(
            (response) => {
                if (live) setState(hasActiveDirectory(response) ? "connected" : "none");
            },
            () => {
                if (live) setState("unknown");
            },
        );
        return () => {
            live = false;
        };
    }, [enabled, state]);
    return enabled && state === "none";
}

export function connectDirectoryParams(prev) {
    const next = new URLSearchParams(prev);
    next.set("mode", "settings");
    next.set("setting", "directory/directories");
    next.delete("adminView");
    next.delete("settingsView");
    next.delete("chat");
    next.delete("cv");
    return next;
}

export function ConnectDirectoryAction({ label = "Connect identity provider" }) {
    const [searchParams, setSearchParams] = useSearchParams();
    return (
        <button
            type="button"
            onClick={() => setSearchParams(connectDirectoryParams(searchParams))}
            className={buttonClass("primary", "sm")}
        >
            {label}
        </button>
    );
}

// The URL mutation behind ConnectEndpointsAction, pure for tests: land on
// Settings' Install section, leaving unrelated params (range, tabs) alone.
// Clears ?chat / ?cv so returning to dashboard mode re-evaluates its
// front door rather than restoring a stale sub-view.
export function connectViewParams(prev) {
    const next = new URLSearchParams(prev);
    next.set("mode", "settings");
    next.set("setting", "endpoints/install");
    next.delete("adminView");
    next.delete("settingsView");
    next.delete("chat");
    next.delete("cv");
    return next;
}

export function ConnectEndpointsAction({ label = "Connect endpoints" }) {
    const [searchParams, setSearchParams] = useSearchParams();
    return (
        <button
            type="button"
            onClick={() => setSearchParams(connectViewParams(searchParams))}
            className={buttonClass("primary", "sm")}
        >
            {label}
        </button>
    );
}
